import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ChartLineUp, ArrowsClockwise, Warning } from "@phosphor-icons/react";

export default function CompRentRadar() {
  const [data, setData] = useState({ below_market: [], total_annual_loss_cents: 0, renewal_window_count: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/comp-rent/below-market"); setData(r.data); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/comp-rent/refresh");
      toast.success(`Refreshed ${r.data.updated} units · ${r.data.source_note}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Refresh failed")); }
    finally { setBusy(false); }
  };

  return (
    <AdminShell>
      <Toaster position="top-right" richColors closeButton/>
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 flex items-center gap-1">
            <ChartLineUp weight="fill" size={12}/> Comp Rent Radar
          </div>
          <h1 className="font-display font-black text-3xl mt-1">Comp Rent Radar</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Every active unit is compared against market rent via Rentcast. Any unit running <b>≥8% below market</b> shows here — sorted by annual $ left on the table. Units with a lease ending in ≤120 days get the 🔥 renewal-window flag.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3 max-w-2xl">
          <div className="rugged-card p-3">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Units below market</div>
            <div className="font-black text-3xl mt-1">{data.below_market.length}</div>
          </div>
          <div className="rugged-card p-3 bg-red-50 border-red-500">
            <div className="text-[9px] font-black uppercase tracking-widest text-red-700">💸 Annual left-on-table</div>
            <div className="font-black text-3xl mt-1 text-red-700">${(data.total_annual_loss_cents/100).toLocaleString(undefined,{maximumFractionDigits:0})}</div>
          </div>
          <div className="rugged-card p-3 bg-amber-50 border-amber-500">
            <div className="text-[9px] font-black uppercase tracking-widest text-amber-800">🔥 Renewal window (≤120d)</div>
            <div className="font-black text-3xl mt-1 text-amber-800">{data.renewal_window_count}</div>
          </div>
        </div>

        <button onClick={refresh} disabled={busy} data-testid="comp-refresh-btn"
          className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2 shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
          <ArrowsClockwise weight="bold" size={14}/> {busy ? "Refreshing…" : "Refresh market rates"}
        </button>

        {data.below_market.length === 0 ? (
          <div className="rugged-card p-8 text-center text-slate-500">
            All active units are at or above market rent. Nice work! 🎉<br/>
            <span className="text-xs">Or hit refresh above to poll fresh market data.</span>
          </div>
        ) : (
          <div className="rugged-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="below-market-table">
              <thead className="bg-slate-100">
                <tr className="text-left">
                  <th className="p-2">Tenant</th>
                  <th className="p-2">Address · Unit</th>
                  <th className="p-2 text-right">Current</th>
                  <th className="p-2 text-right">Market</th>
                  <th className="p-2 text-right">% Below</th>
                  <th className="p-2 text-right">Annual loss</th>
                  <th className="p-2">Lease end</th>
                </tr>
              </thead>
              <tbody>
                {data.below_market.map(r => (
                  <tr key={r.id} className={`border-t border-slate-200 ${r.renewal_window ? "bg-amber-50" : ""}`} data-testid={`below-row-${r.id}`}>
                    <td className="p-2 font-bold">{r.tenant_name}</td>
                    <td className="p-2 max-w-[240px] truncate">{r.property_address} · #{r.unit || "—"}</td>
                    <td className="p-2 text-right font-mono">${(r.current_rent_cents/100).toFixed(0)}</td>
                    <td className="p-2 text-right font-mono">${(r.market_rent_cents/100).toFixed(0)}</td>
                    <td className="p-2 text-right">
                      <span className={`px-1.5 py-0.5 text-white text-[9px] font-black uppercase tracking-widest ${r.delta_pct >= 15 ? "bg-red-700" : "bg-amber-500"}`}>
                        -{r.delta_pct}%
                      </span>
                    </td>
                    <td className="p-2 text-right font-mono font-black text-red-700">${(r.annual_loss_cents/100).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                    <td className="p-2 text-[11px]">
                      {r.lease_end ? (
                        <span className={r.renewal_window ? "font-black text-amber-800" : "text-slate-500"}>
                          {r.renewal_window && "🔥 "}{r.lease_end} {r.days_to_renewal !== null && `(${r.days_to_renewal}d)`}
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
